import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

type SectionCardProps = {
  /** Header title (uppercase tracking) */
  title?: string;
  /** Icon rendered in brand color before the title */
  icon?: ReactNode;
  /** Optional action button in the header */
  action?: { label: string; onClick: () => void };
  /** Render a custom header instead of the default title/icon/action bar */
  header?: ReactNode;
  /** Remove body padding (for full-width content like lists/tables) */
  noPadding?: boolean;
  /** Optional class name for card root */
  className?: string;
  /** Optional class name for body wrapper */
  bodyClassName?: string;
  children: ReactNode;
};

export function SectionCard({
  title,
  icon,
  action,
  header,
  noPadding,
  className,
  bodyClassName,
  children,
}: SectionCardProps) {
  const showDefaultHeader = !header && title;

  return (
    <div
      className={cn("relative rounded-xl border border-surface-800/40 overflow-hidden", className)}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-surface-800/80 via-surface-850 to-surface-800/60" />
      <div className="relative">
        {header && <div className="border-b border-separator">{header}</div>}
        {showDefaultHeader && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-separator">
            <div className="flex items-center gap-2">
              {icon && <span className="text-brand-400">{icon}</span>}
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h3>
            </div>
            {action && (
              <button
                onClick={action.onClick}
                className="text-xs font-medium text-surface-400 hover:text-brand-400 transition-colors"
              >
                {action.label}
              </button>
            )}
          </div>
        )}
        <div className={cn(noPadding ? "" : "p-4", bodyClassName)}>{children}</div>
      </div>
    </div>
  );
}
